import type { QueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { getConfig } from '@/config/config';
import { useConnectionStore, type Namespace } from '@/state/connectionStore';
import { useSessionStore } from '@/state/sessionStore';
import { bindingsFor } from './bindings';

/**
 * The two realtime namespaces (contracts/realtime-events.md), open while a
 * member is signed in. The browser authenticates the handshake with the
 * session cookie (`withCredentials`), so the page never holds a token.
 * After a dropped connection comes back, everything on screen is refetched,
 * so nothing sent meanwhile is missed (WEB-010).
 */
const NAMESPACES: Record<Namespace, string> = { messaging: '/ws/messaging', calls: '/ws/calls' };
const sockets: Partial<Record<Namespace, Socket>> = {};

export function getSocket(namespace: Namespace): Socket | null {
  return sockets[namespace] ?? null;
}

function open(namespace: Namespace, queryClient: QueryClient): Socket {
  const { apiUrl } = getConfig();
  const socket = io(`${apiUrl}${NAMESPACES[namespace]}`, {
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  const { setStatus } = useConnectionStore.getState();
  let wasConnected = false;

  setStatus(namespace, 'connecting');
  socket.on('connect', () => {
    setStatus(namespace, 'online');
    if (wasConnected) {
      void queryClient.invalidateQueries({ refetchType: 'active' });
    } else if (namespace === 'messaging') {
      // Something may have been sent between loading the page and this first connection.
      void queryClient.invalidateQueries({ queryKey: ['messages'], refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: ['conversations'], refetchType: 'active' });
    }
    wasConnected = true;
  });
  socket.on('disconnect', () => setStatus(namespace, 'offline'));
  socket.on('connect_error', () => setStatus(namespace, wasConnected ? 'offline' : 'connecting'));

  for (const { event, handler } of bindingsFor(namespace)) {
    socket.on(event, (payload: unknown) => handler(payload, { queryClient, socket }));
  }
  return socket;
}

/**
 * The browser knows at once when the network goes: say so straight away
 * (socket.io would only notice after its ping times out). When it is back,
 * start fresh connections: the old ones may never have noticed, and a new
 * connection refetches whatever was missed (WEB-010).
 */
function onNetworkLost(): void {
  const { setStatus } = useConnectionStore.getState();
  for (const namespace of Object.keys(NAMESPACES) as Namespace[]) {
    if (sockets[namespace]) setStatus(namespace, 'offline');
  }
}

function onNetworkBack(): void {
  for (const namespace of Object.keys(NAMESPACES) as Namespace[]) {
    sockets[namespace]?.disconnect().connect();
  }
}

function listenToNetwork(listen: boolean): void {
  if (typeof window === 'undefined') return;
  const method = listen ? 'addEventListener' : 'removeEventListener';
  window[method]('offline', onNetworkLost);
  window[method]('online', onNetworkBack);
}

function closeAll(): void {
  for (const namespace of Object.keys(NAMESPACES) as Namespace[]) {
    sockets[namespace]?.disconnect();
    sockets[namespace] = undefined;
  }
  useConnectionStore.getState().reset();
}

/** Opens the sockets while signed in and closes them on sign-out. Returns a stop function. */
export function connectSocketsToSession(queryClient: QueryClient): () => void {
  const sync = (signedIn: boolean) => {
    if (signedIn && !sockets.messaging) {
      for (const namespace of Object.keys(NAMESPACES) as Namespace[]) {
        sockets[namespace] = open(namespace, queryClient);
      }
      listenToNetwork(true);
    } else if (!signedIn && sockets.messaging) {
      listenToNetwork(false);
      closeAll();
    }
  };
  sync(useSessionStore.getState().status === 'signedIn');
  const unsubscribe = useSessionStore.subscribe((state) => sync(state.status === 'signedIn'));
  return () => {
    unsubscribe();
    listenToNetwork(false);
    closeAll();
  };
}
